import { useQuery, QueryObserverResult } from '@tanstack/react-query';
import { isBefore } from 'date-fns';
import { isNumber, sortBy } from 'lodash-es';
import { useMemo } from 'react';
import {
	EventPriceDto,
	EventPriceTypesEnum,
	EventTicketsReservationDto,
	EventTicketsTotalsWithReservationsDto,
	isPriceSoldOut,
	getSpotsLeftText,
} from '@sweatpals/shared';
import { queryKeys } from '../../constants';
import { eventTicketsReservationService } from '../../services';
import { EventType } from '../../types/event-types';

export interface QueryEventReservedSpotsProps {
	eventId: string;
	instance: string;
	eventData?: EventType;
}

export const useQueryEventReservedSpots = (props: QueryEventReservedSpotsProps, refetchInterval = 5000): {
	prices: EventPriceDto[];
	publishedPrices: EventPriceDto[];
	availablePrices: EventPriceDto[];
	availablePublishedPrices: EventPriceDto[];
	isEventSoldOut: boolean;
	freeSpotsCount?: number;
	spotsLeftText?: string;
	eventTicketsCountAndSpots?: EventTicketsTotalsWithReservationsDto;
	userTicketReservations?: EventTicketsReservationDto[];
	refetchEventTicketsCountAndSpots(): Promise<QueryObserverResult<EventTicketsTotalsWithReservationsDto>>;
	refetchUserEventTicketReservations(): Promise<QueryObserverResult<EventTicketsReservationDto[]>>;
	isUserTicketReservationsFetching: boolean;
} => {
	const { eventId, instance, eventData } = props;

	const { data: eventTicketsCountAndSpots, refetch: refetchEventTicketsCountAndSpots } = useQuery(
		queryKeys.eventTicketsCountAndSpots(eventId, instance),
		async () => eventTicketsReservationService.getEventTicketsCountAndSpotsWithReservations(eventId, instance),
		{
			enabled: !!eventId && !!instance,
			refetchInterval,
		}
	);
	const { data: userTicketReservations, refetch: refetchUserEventTicketReservations, isFetching: isUserTicketReservationsFetching } = useQuery(
		queryKeys.userEventTicketReservations(eventId, instance),
		async () => eventTicketsReservationService.getUserEventTicketReservations(eventId, instance),
		{
			enabled: !!eventId && !!instance,
		}
	);

	const validTicketReservations = useMemo(
		() => (userTicketReservations ?? []).filter(({ expiresAt }) => isBefore(new Date(), expiresAt)),
		[userTicketReservations]
	);
	const freeSpotsCount = useMemo(
		() => eventTicketsCountAndSpots && eventTicketsCountAndSpots.allSpotsCountWithReservations + validTicketReservations.length,
		[eventTicketsCountAndSpots, validTicketReservations.length]
	);
	const isEventSoldOut = useMemo(() => (isNumber(freeSpotsCount) ? freeSpotsCount <= 0 : false), [freeSpotsCount]);
	const spotsLeftText = useMemo(
		() => eventTicketsCountAndSpots && getSpotsLeftText(freeSpotsCount, eventTicketsCountAndSpots.ticketsLimit),
		[eventTicketsCountAndSpots, freeSpotsCount]
	);

	const prices = useMemo(
		() => sortBy((eventData?.prices ?? []), price => isPriceSoldOut(price, eventTicketsCountAndSpots, validTicketReservations)),
		[eventData, eventTicketsCountAndSpots, validTicketReservations]
	);
	const availablePrices = useMemo(
		() => prices.filter(price => !isPriceSoldOut(price, eventTicketsCountAndSpots, validTicketReservations)),
		[prices, validTicketReservations, eventTicketsCountAndSpots]
	);
	const publishedPrices = useMemo(
		() => (prices.filter(p => p.tierTypeId !== EventPriceTypesEnum.Unlisted) ?? []),
		[prices]
	);
	const availablePublishedPrices = useMemo(
		() => (availablePrices.filter(p => p.tierTypeId !== EventPriceTypesEnum.Unlisted) ?? []),
		[availablePrices]
	);

	return {
		prices,
		publishedPrices,
		availablePrices,
		availablePublishedPrices,
		freeSpotsCount,
		isEventSoldOut,
		spotsLeftText,
		refetchEventTicketsCountAndSpots,
		refetchUserEventTicketReservations,
		eventTicketsCountAndSpots,
		userTicketReservations: validTicketReservations,
		isUserTicketReservationsFetching,
	};
};
